// src/components/workspace/session/SessionPreflight.js

import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { CHECK_STATUS, runSessionPreflight } from "../../../utils/sessionPreflight";

/**
 * Runs the device and relay checks before a session starts, and shows the
 * result plainly enough that the user can fix the problem themselves.
 *
 * The important behaviour is what happens on failure: a hard failure blocks the
 * call, because starting one anyway means the other party joins a session that
 * cannot work and the incident loses ten minutes to confusion.
 */

const STATUS_ICONS = {
  [CHECK_STATUS.PASS]: { Icon: CheckCircle2, className: "text-emerald-600" },
  [CHECK_STATUS.WARN]: { Icon: AlertTriangle, className: "text-amber-500" },
  [CHECK_STATUS.FAIL]: { Icon: XCircle, className: "text-rose-600" },
};

export default function SessionPreflight({ onReady, onCancel, autoRun = true }) {
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    setRunning(true);
    setResult(null);
    const outcome = await runSessionPreflight();
    setResult(outcome);
    setRunning(false);
  }, []);

  useEffect(() => {
    if (autoRun) run();
  }, [autoRun, run]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-5">
      <div>
        <p className="text-sm font-semibold text-slate-900">
          Checking your setup
        </p>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          A quick test of your devices and network before the other party is
          called.
        </p>
      </div>

      {running && !result ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          Running checks…
        </div>
      ) : (
        <ul className="space-y-2.5">
          {(result?.checks || []).map((check) => {
            const { Icon, className } =
              STATUS_ICONS[check.status] || STATUS_ICONS[CHECK_STATUS.WARN];
            return (
              <li key={check.id} className="flex items-start gap-2.5">
                <Icon className={`mt-0.5 h-4 w-4 flex-shrink-0 ${className}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {check.label}
                  </p>
                  <p className="text-xs leading-relaxed text-slate-500">
                    {check.detail}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {result && (
        <div className="mt-auto flex flex-wrap gap-2">
          {result.blocked ? (
            <>
              <button
                type="button"
                onClick={run}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
              >
                Run check again
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-50"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => onReady(result)}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                Start session
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-300 transition hover:bg-slate-50"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
